import AutoInvoice from "@/types/AutoInvoice";
import Badge from "@/components/atoms/Badge";
import { formatNumber } from "@/lib/utils";
import { toUtcDate } from "@/lib/utils";

type Props = {
  autoInvoice: AutoInvoice | null;
};

const Detail = ({ autoInvoice }: Props) => {
  const isOldData = !autoInvoice?.calculated_data_html.includes("w-full border-collapse")

  const cleanedHtml = autoInvoice?.calculated_data_html || "";
  let transformedHtml;
  const uncheckedColumns = autoInvoice?.unchecked_columns || [];
  if (isOldData && cleanedHtml) {
    // Regex to transform the old HTML into a table format

    let rowsHtml = cleanedHtml
    .replace(/sowId/g, 'p-2 checkbox-label')
    .replace(/servicePeriod/g, 'p-2 checkbox-label')
    .replace(/serviceType/g, 'p-2 checkbox-label')
    .replace(/rate/g, 'p-2 checkbox-label text-right')
    .replace(/description/g, 'p-2 checkbox-label text-center')
    .replace(/hours/g, 'p-2 checkbox-label text-right')
    .replace(/subAmount/g, 'p-2 checkbox-label text-right')
    .replace(/totalAmount/g, 'p-2 checkbox-label text-right');
  


    transformedHtml = `
      <table class="w-full border-collapse text-sm" style="table-layout: fixed;">
        <thead class="border-b-8 border-border">
          <tr>
            ${!uncheckedColumns.includes('sowId') ? `<th class="p-2 text-left min-w-36">Sow ID</th>` : ''}
            ${!uncheckedColumns.includes('servicePeriod') ? `<th class="p-2 text-left min-w-36">Service Period</th>` : ''}
            ${!uncheckedColumns.includes('serviceType') ? `<th class="p-2 text-left min-w-64">Service Type</th>` : ''}
            ${!uncheckedColumns.includes('serviceType') && !uncheckedColumns.includes('rate') ? `<th class="p-2 text-right min-w-64">Rate</th>` : ''}
            ${!uncheckedColumns.includes('description') ? `<th class="p-2 text-center">Description</th>` : ''}
            ${!uncheckedColumns.includes('serviceType') && !uncheckedColumns.includes('hours') ? `<th class="p-2 text-right min-w-64">Monthly Hours/
Output Count</th>` : ''}
            ${!uncheckedColumns.includes('serviceType') && !uncheckedColumns.includes('subAmount') ? `<th class="p-2 text-right min-w-36">Sub-Amount</th>` : ''}
            ${!uncheckedColumns.includes('totalAmount') ? `<th class="p-1 text-right min-w-36">Amount</th>` : ''}
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
        </tbody>
      </table>`;
  } else {
    transformedHtml = cleanedHtml
      ?.replace(/<span>\s*on\s*<\/span>/g, '')
      .replace(/class="([^"]*?)"/g, (match: string, p1: string) => {
        const newClasses = p1
          .split(' ')
          .filter((cls: string) => cls !== 'cursor-pointer' && cls !== 'hover:bg-slate-300')
          .concat(['checkbox-label'])
          .join(' ');
        return `class="${newClasses}"`;
      })
      .replace(/<button(.*?)>/g, '<button$1 style="pointer-events: none; cursor: default;">')
      .replace('flex items-center mb-4 checkbox-label', 'flex items-center mb-4 checkbox-label hidden')
      .replace(/<td[^>]*class="[^"]*delete-button[^"]*"[^>]*>.*?<\/td>/g, '');
  }


  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute('content') || '' : '';
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(toUtcDate(dateString));
  };

  return (
    <div className="space-y-6">
      <div className="shadow p-6">
        <span className="block text-xl font-bold mb-4">Approval Details</span>
        <div className="grid grid-cols-4 gap-5">
          <div>
            <span className="text-sm font-light block">Status</span>
            <Badge
              variant={
                autoInvoice?.status === 'APPROVED'
                  ? 'success'
                  : autoInvoice?.status === 'DECLINED'
                  ? 'destructive'
                  : autoInvoice?.status === 'WAITING APPROVAL'
                  ? 'primary'
                  : 'secondary'
              }
            >
              {autoInvoice?.status}
            </Badge>
          </div>
          <div>
            <span className="text-sm font-light block">Created By</span>
            <span className="font-bold block">{autoInvoice?.created_by.name}</span>
          </div>
          {(autoInvoice?.status === 'APPROVED' || autoInvoice?.status === 'DECLINED') && (
            <div>
              <span className="text-sm font-light block">
                {autoInvoice?.status === 'APPROVED' ? 'Approved By' : autoInvoice?.status === 'DECLINED' ? 'Declined By' : ''}
              </span>
              <span className="font-bold block">
                {autoInvoice?.status === 'APPROVED'
                  ? autoInvoice?.approved_by?.name
                  : autoInvoice?.status === 'DECLINED'
                  ? autoInvoice?.declined_by?.name
                  : ''}
              </span>
            </div>
          )}
          {(autoInvoice?.status === 'APPROVED' || autoInvoice?.status === 'DECLINED') && (
            <div>
              <span className="text-sm font-light block">
                {autoInvoice?.status === 'APPROVED' ? 'Approved At' : autoInvoice?.status === 'DECLINED' ? 'Declined Reason' : ''}
              </span>
              <span className="font-bold block">
                {autoInvoice?.status === 'APPROVED'
                  ? autoInvoice?.approved_at || 'Not Defined'
                  : autoInvoice?.status === 'DECLINED'
                  ? autoInvoice?.declined_reason || 'Not Defined'
                  : ''}
              </span>
            </div>
          )}
          {autoInvoice?.status === 'WAITING APPROVAL' && autoInvoice?.reason && (
            <div>
              <span className="text-sm font-light block">
                Amount Difference Reason
              </span>
              <span className="font-bold block">
                {autoInvoice?.reason}
              </span>
            </div>
          )}
        </div>
      </div>
      
      <div className="shadow p-6">
        <span className="block text-xl font-bold mb-4">Client Details</span>
        <div className="grid grid-cols-6 gap-12">
          <div>
            <span className="text-sm font-light block">Client</span>
            <span className="font-bold block">{autoInvoice?.account.name}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Client Entity</span>
            <span className="font-bold block">{autoInvoice?.entity?.name}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Client Contact</span>
            <span className="font-bold block">{autoInvoice?.client_contact}</span>
          </div>
          <div className="col-span-2">
            <span className="text-sm font-light block">Client Address</span>
            <span className="font-bold block">{autoInvoice?.client_address}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Client VAT</span>
            <span className="font-bold block">{autoInvoice?.client_vat}</span>
          </div>
        </div>
      </div>

      <div className="shadow p-6">
        <span className="block text-xl font-bold mb-4">{autoInvoice?.invoice_type} Details</span>
        <div className="grid grid-cols-6 gap-12">
          <div>
            <span className="text-sm font-light block">Date</span>
            <span className="font-bold block">{autoInvoice?.date}</span>
          </div>
          <div>
            <span className="text-sm font-light block">NMQ Entity</span>
            <span className="font-bold block">{autoInvoice?.nmq_entity?.name}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Service Period</span>
            <span className="font-bold block">
              {autoInvoice?.service_period_start && autoInvoice?.service_period_end &&
                `${formatDate(autoInvoice.service_period_start)} - ${formatDate(autoInvoice.service_period_end)}`}
            </span>
          </div>
          
          <div className="col-span-2">
            <span className="text-sm font-light block">NMQ Address</span>
            <span className="font-bold block">{autoInvoice?.nmq_entity?.address}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Reference No</span>
            <span className="font-bold block">{autoInvoice?.reference_no}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Order/PO Number</span>
            <span className="font-bold block">{autoInvoice?.order_ref_no}</span>
          </div>
          
          <div className="col-span-1">
            <span className="text-sm font-light block">Invoicing Queries</span>
            <span className="font-bold block">{autoInvoice?.invoicing_queries}</span>
          </div>
          <div className="col-span-2">
            <span className="text-sm font-light block">SOWs</span>
            <span className="font-bold block">
              {autoInvoice?.sows?.map((sow) => sow.name).join(", ")}
            </span>
          </div>
          <div>
            <span className="text-sm font-light block">Payment Terms</span>
            <span className="font-bold block">{autoInvoice?.payment_terms}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Exact Invoice ID</span>
            <span className="font-bold block">{autoInvoice?.exact_invoice_id}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Total Sub Amount</span>
            <span className="font-bold block">{`${['MANUAL CREDIT NOTE', 'CREDIT NOTE'].includes(autoInvoice?.invoice_type || '') ? '-' : ''} ${formatNumber(autoInvoice?.total_sub_amount || 0)}`}</span>
          </div>
          <div>
            <span className="text-sm font-light block">Additional discount/charge</span>
            <span className="font-bold block">{`${autoInvoice?.additional_type === 'ADDITIONAL DISCOUNT' ? '-' : ''} ${formatNumber(autoInvoice?.additional_cost_discount || 0)}`}</span>
          </div>
          {autoInvoice?.additional_amount_description && (
            <div className="col-span-2">
              <span className="text-sm font-light block">Discount/Charge Description</span>
              <span className="font-bold block">{autoInvoice?.additional_amount_description}</span>
            </div>
          )}
          <div>
            <span className="text-sm font-light block">VAT Amount</span>
            <span className="font-bold block">{`${['MANUAL CREDIT NOTE', 'CREDIT NOTE'].includes(autoInvoice?.invoice_type || '') ? '-' : ''} ${formatNumber(autoInvoice?.vat_amount || 0)}`}</span>
          </div>
          
          <div>
            <span className="text-sm font-light block">Total Amount</span>
            <span className="font-bold block">{`${['MANUAL CREDIT NOTE', 'CREDIT NOTE'].includes(autoInvoice?.invoice_type || '') ? '-' : ''} ${formatNumber(autoInvoice?.total_amount || 0)}`}</span>
          </div>
        </div>
      </div>

      <div className="shadow p-6">
        <span className="block text-xl font-bold mb-4">{autoInvoice?.invoice_type} Table</span>
        <div className="grid grid-cols-6 gap-12">
          {autoInvoice?.calculated_data_html && (
            <div
              className="col-span-6 w-full overflow-auto"
              dangerouslySetInnerHTML={{ __html: transformedHtml }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Detail;
